import React, { useEffect, useState, useCallback } from "react"
import { Box, Tooltip, IconButton } from "@mui/material";
import { HelpOutline as HelpOutlineIcon } from "@mui/icons-material";
import SideMenuContainer from "components/shared/SideMenuContainer";
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import TourOutline from 'components/shared/TourOutline';
import { useEntityTour } from 'hooks/useTour';
import { DASHBOARD_TOUR, DEFAULT_TOUR_STYLES, TOUR_TITLES, CustomTooltip } from 'config/tours';
import { useSelector } from 'react-redux';
import { organizationState } from 'redux/organizationSlice';

import DashboardHome from "./DashboardHome"
import QuarterlyComplianceReport from "./QuarterlyComplianceReport"
import EmailActivity from "./EmailActivity"
import MyActivity from "./MyActivity"
import { useLocation, useNavigate } from "react-router-dom";

const menuItems = [
  { key: 'my-activity', label: 'Activity', className: 'dashboard-menu-my-activity' },
  { key: 'compliance', label: "Compliance", className: 'dashboard-menu-compliance' },
  { key: 'quarterly', label: 'Expiring COIs', className: 'dashboard-menu-quarterly' },
  { key: 'email-activity', label: 'Email Activity', className: 'dashboard-menu-email-activity' }
]

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedClient } = useSelector(organizationState);
  const activeItem = String(location.hash).replace("#/", "");

  // Tour state
  const [tourKey, setTourKey] = useState(0);
  const {
    isRunning: isTourRunning,
    stepIndex: tourStepIndex,
    completedSteps,
    startTour,
    stopTour,
    goToStep,
  } = useEntityTour(DASHBOARD_TOUR.id, selectedClient?.clientID);

  useEffect(() => {
    if (!location.hash) {
      navigate(location.pathname + `#/${menuItems[0].key}`);
    }
  }, [location.pathname])

  // Navigate to vertical tab (sidebar)
  const navigateToVerticalTab = useCallback((tabKey) => {
    navigate(location.pathname + `#/${tabKey}`);
  }, [navigate, location.pathname]);

  // Tour callback - handles navigation between vertical tabs and sub-tabs
  const handleTourCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Handle tour completion
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour();
      return;
    }

    // Handle close action
    if (action === ACTIONS.CLOSE) {
      stopTour();
      return;
    }

    // Calculate next step based on action
    if (type === EVENTS.STEP_AFTER) {
      const isGoingBack = action === ACTIONS.PREV;
      const nextStepIndex = isGoingBack ? index - 1 : index + 1;

      // Bounds check
      if (nextStepIndex < 0 || nextStepIndex >= DASHBOARD_TOUR.steps.length) {
        if (nextStepIndex >= DASHBOARD_TOUR.steps.length) {
          stopTour();
        }
        return;
      }

      const nextStep = DASHBOARD_TOUR.steps[nextStepIndex];

      // Handle vertical tab switching
      if (nextStep?.data?.verticalTab) {
        const targetTab = nextStep.data.verticalTab;
        if (targetTab !== activeItem) {
          navigateToVerticalTab(targetTab);
          // Email activity needs longer delay due to data loading
          const delay = targetTab === 'email-activity' ? 800 : 300;
          setTimeout(() => {
            goToStep(nextStepIndex);
          }, delay);
          return;
        }
      }

      // Normal step navigation
      goToStep(nextStepIndex);
    }
  }, [activeItem, goToStep, stopTour, navigateToVerticalTab]);

  // Start tour handler
  const handleStartTour = useCallback(() => {
    // Start on Activity tab
    navigateToVerticalTab('my-activity');
    setTourKey(prev => prev + 1);
    setTimeout(() => {
      startTour();
    }, 200);
  }, [startTour, navigateToVerticalTab]);

  // Handle clicking on steps in the tour outline
  const handleStepClick = useCallback((index) => {
    const step = DASHBOARD_TOUR.steps[index];
    if (step?.data?.verticalTab) {
      const targetTab = step.data.verticalTab;
      if (targetTab !== activeItem) {
        navigateToVerticalTab(targetTab);
        // Email activity needs longer delay due to data loading
        const delay = targetTab === 'email-activity' ? 800 : 300;
        setTimeout(() => {
          goToStep(index);
        }, delay);
        return;
      }
    }
    requestAnimationFrame(() => {
      setTimeout(() => {
        goToStep(index);
      }, 50);
    });
  }, [activeItem, goToStep, navigateToVerticalTab]);

  return (
    <Box className="dashboard-container" sx={{ position: 'relative' }}>
      {/* Help button - positioned top right */}
      <Tooltip title="Take a tour of the Dashboard">
        <IconButton
          onClick={handleStartTour}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>

      <SideMenuContainer items={menuItems} menuClassName="dashboard-side-menu">
        {activeItem === menuItems[0].key && <MyActivity isTourRunning={isTourRunning} tourStepIndex={tourStepIndex} />}
        {activeItem === menuItems[1].key && <DashboardHome />}
        {activeItem === menuItems[2].key && <QuarterlyComplianceReport />}
        {activeItem === menuItems[3].key && <EmailActivity isTourRunning={isTourRunning} tourStepIndex={tourStepIndex} />}
      </SideMenuContainer>

      {/* Joyride Tour */}
      <Joyride
        key={`dashboard-tour-${tourKey}`}
        steps={DASHBOARD_TOUR.steps}
        stepIndex={tourStepIndex}
        run={isTourRunning}
        continuous
        showSkipButton
        showProgress
        disableScrolling={false}
        disableScrollParentFix
        spotlightClicks
        callback={handleTourCallback}
        tooltipComponent={CustomTooltip}
        styles={DEFAULT_TOUR_STYLES}
      />

      {/* Tour Outline Sidebar */}
      {isTourRunning && (
        <TourOutline
          steps={DASHBOARD_TOUR.steps}
          currentStep={tourStepIndex}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          title={TOUR_TITLES[DASHBOARD_TOUR.id]}
        />
      )}
    </Box>
  )
}

export default Dashboard;
